/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line, newline-per-chained-call, react/jsx-wrap-multilines, implicit-arrow-linebreak, function-paren-newline, operator-linebreak, react/jsx-curly-newline */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import Switch from '@mui/material/Switch';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Search from '@mui/icons-material/Search';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CustomTable from '../../components/table/CustomTable';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

/*
 * Settings → Chart Abstraction. Org-wide on/off master switch + a per-member
 * grid granting Chart Tracking (rep) and/or QA Review access. Backed by
 * /workspace/:id/chart-abstraction/settings + /access/:memberId.
 */

const TABLE_HEADERS = [
  { label: 'Member', colSpan: 2 },
  { label: 'Designation', colSpan: 1 },
  { label: 'Chart Tracking', colSpan: 1 },
  { label: 'QA Review', colSpan: 1 },
];

const initials = (name) => name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

function ChartAbstraction() {
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const workspaceId = activeWorkspace?.id;

  const [enabled, setEnabled] = useState(false);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingFeature, setSavingFeature] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let active = true;
    (async () => {
      setLoading(true);
      const res = await sendRequest({ method: 'get', url: API.CHART_ABSTRACTION.settings(workspaceId) });
      if (!active) return;
      if (res.success) {
        setEnabled(!!res.data.enabled);
        setMembers(res.data.members || []);
      } else {
        displayNotification({ message: res.data?.error || 'Failed to load Chart Abstraction settings', severity: 'error' });
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [workspaceId]);

  const toggleFeature = async () => {
    if (savingFeature) return;
    const next = !enabled;
    setEnabled(next); // optimistic
    setSavingFeature(true);
    const res = await sendRequest({ method: 'patch', url: API.CHART_ABSTRACTION.settings(workspaceId), data: { enabled: next } });
    setSavingFeature(false);
    if (res.success) {
      displayNotification({ message: `Chart Abstraction ${next ? 'enabled' : 'turned off'} for your organization`, severity: next ? 'success' : 'info' });
    } else {
      setEnabled(!next); // revert
      displayNotification({ message: res.data?.error || 'Could not update the setting', severity: 'error' });
    }
  };

  const toggleAccess = async (memberId, key) => {
    const target = members.find((m) => m.memberId === memberId);
    if (!target) return;
    const nextVal = !target[key];
    setMembers((prev) => prev.map((m) => (m.memberId === memberId ? { ...m, [key]: nextVal } : m))); // optimistic
    const res = await sendRequest({ method: 'patch', url: API.CHART_ABSTRACTION.access(workspaceId, memberId), data: { [key]: nextVal } });
    if (res.success) {
      displayNotification({
        message: `${target.name} ${nextVal ? 'can now access' : 'no longer has access to'} ${key === 'chartTracking' ? 'Chart Tracking' : 'QA Review'}`,
        severity: 'success',
      });
    } else {
      setMembers((prev) => prev.map((m) => (m.memberId === memberId ? { ...m, [key]: !nextVal } : m))); // revert
      displayNotification({ message: res.data?.error || 'Could not update access', severity: 'error' });
    }
  };

  const chartCount = members.filter((m) => m.chartTracking).length;
  const qaCount = members.filter((m) => m.qaReview).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => m.name.toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
  }, [members, search]);

  const items = filtered.map((m) => ({
    member: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', background: 'var(--blue-7, #D8E6FC)', color: 'var(--primary, #213D69)', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
          {initials(m.name)}
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>{m.name}</span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--dark-3, #546077)' }}>{m.email}</span>
        </span>
      </div>
    ),
    designation: <span style={{ fontSize: 14, color: 'var(--dark-2, #293855)' }}>{m.designation}</span>,
    chartTracking: <Switch checked={!!m.chartTracking} disabled={!enabled} onChange={() => toggleAccess(m.memberId, 'chartTracking')} color="primary" />,
    qaReview: <Switch checked={!!m.qaReview} disabled={!enabled} onChange={() => toggleAccess(m.memberId, 'qaReview')} color="primary" />,
  }));

  return (
    <div style={{ padding: '20px 4px', fontFamily: 'Inter, sans-serif' }}>
      {/* Org master switch */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 18px', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 10, marginBottom: 22, background: enabled ? '#fff' : 'var(--dark-10, #FAFAFA)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>Chart Abstraction</span>
            <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '2px 8px', ...(enabled ? { background: '#E7F6E6', color: 'var(--green-1, #256D23)' } : { background: 'var(--dark-7, #ECECF0)', color: 'var(--dark-3, #546077)' }) }}>
              {enabled ? 'On' : 'Off'}
            </span>
          </div>
          <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 0', maxWidth: 640 }}>
            Turn the chart tracking &amp; QA review workflow on or off for your entire organization. When off, no one can log chart time or review — regardless of individual access below.
          </p>
        </div>
        <Switch checked={enabled} disabled={loading || savingFeature} onChange={toggleFeature} color="primary" />
      </div>

      {/* Member access */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>Member access</h4>
          <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 0', maxWidth: 560 }}>
            Grant members access to the Chart Tracking timer and/or the QA Review queue.
          </p>
        </div>
        <TextField
          size="small"
          placeholder="Search members"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search style={{ fontSize: 18, color: 'var(--dark-4, #7D8698)' }} /></InputAdornment>) }}
          sx={{ width: 240 }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, marginBottom: 8, background: 'var(--dark-9, #FBFBFC)', border: '1px solid var(--dark-6, #E0E1E6)' }}>
        <InfoOutlined style={{ fontSize: 18, color: 'var(--primary, #213D69)', flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 14, color: 'var(--dark-2, #293855)', margin: 0 }}>
          <strong>Chart Tracking</strong>
          {' lets a member log time on charts in the desktop timer. '}
          <strong>QA Review</strong>
          {' lets them approve or deny completed charts. '}
          <span style={{ color: 'var(--dark-3, #546077)' }}>{`${chartCount} with Chart Tracking · ${qaCount} with QA Review`}</span>
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><CircularProgress size={26} /></div>
      ) : (
        <CustomTable headers={TABLE_HEADERS} items={items} noItemText="No members match your search." />
      )}
    </div>
  );
}

export default ChartAbstraction;
